/**
 * Length display shared by the plan canvas, panels, and dims: feet and
 * inches, inches, or decimal feet, in the chosen units.
 */

export const fmt = (n: number): string => {
  const r = Math.round(n * 10) / 10;
  return Number.isFinite(r) ? String(r) : '—';
};

/**
 * Split a decimal-feet length into whole feet + inches, rounded to the
 * nearest `inchIncrement` (default the half inch, the model's resolution;
 * pass 0.1 for editing so a typed tenth shows as typed), carrying into feet.
 */
export const splitFeetInches = (lengthFeet: number, inchIncrement = 0.5): { feet: number; inches: number } => {
  const unitsPerInch = 1 / inchIncrement;
  const unitsPerFoot = 12 * unitsPerInch;
  const totalUnits = Math.round(lengthFeet * unitsPerFoot);
  return { feet: Math.trunc(totalUnits / unitsPerFoot), inches: Math.abs(totalUnits % unitsPerFoot) / unitsPerInch };
};

/** The one-character half glyph a half inch displays as. */
export const HALF_GLYPH = '½';

/**
 * Inches for display: whole inches as the number, a half as the ½ glyph
 * (`4½`, and `½` alone under an inch). Any other fraction (a tenth from an
 * edit field) shows as its decimal.
 */
export const formatInches = (inches: number): string => {
  const whole = Math.trunc(inches);
  if (Math.abs(inches - whole - 0.5) > 1e-9) return String(inches);
  return whole === 0 ? HALF_GLYPH : `${whole}${HALF_GLYPH}`;
};

/**
 * Architectural feet-and-inches display: `10' 4"`, a half inch as `10' 4½"`;
 * inches omitted when zero: `10'`. Shows the magnitude: a negative distance (e.g. a free element
 * overhanging the room rectangle) displays as the equivalent positive one.
 */
export const formatFeetInches = (lengthFeet: number): string => {
  if (!Number.isFinite(lengthFeet)) return '—';
  const { feet, inches } = splitFeetInches(Math.abs(lengthFeet));
  return inches === 0 ? `${feet}'` : `${feet}' ${formatInches(inches)}"`;
};

/**
 * How lengths display and are typed. A string union so metric units can join
 * later: every consumer formats through `formatLength` and the length input
 * lays itself out per unit, so adding one is an entry here and a case in each.
 */
export type MeasurementUnits = 'feetInches' | 'inches' | 'feet';

export const DEFAULT_MEASUREMENT_UNITS: MeasurementUnits = 'feetInches';

/** The units a user may choose, in the order the settings card lists them. */
export const MEASUREMENT_UNITS: readonly { id: MeasurementUnits; label: string }[] = [
  { id: 'feetInches', label: 'Feet and inches' },
  { id: 'inches', label: 'Inches' },
  { id: 'feet', label: 'Feet' },
];

export const isMeasurementUnits = (value: unknown): value is MeasurementUnits => MEASUREMENT_UNITS.some(units => units.id === value);

/** A length as total inches, to the half inch. */
export const toHalfInches = (lengthFeet: number): number => Math.round(lengthFeet * 24) / 2;

/** Decimal feet for display: two places, or none for a whole foot (`10.38`, `10.50`, `10`). Magnitude. */
export const formatDecimalFeet = (lengthFeet: number): string => {
  const rounded = Math.round(Math.abs(lengthFeet) * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

/** A length in the chosen units: `10' 4½"`, `124½"`, or `10.38'`. Magnitude, like formatFeetInches. */
export function formatLength(lengthFeet: number, units: MeasurementUnits): string {
  if (!Number.isFinite(lengthFeet)) return '—';
  switch (units) {
    case 'feetInches':
      return formatFeetInches(lengthFeet);
    case 'inches':
      return `${formatInches(toHalfInches(Math.abs(lengthFeet)))}"`;
    case 'feet':
      return `${formatDecimalFeet(lengthFeet)}'`;
  }
}

/**
 * A length row's text, as a read-only panel shows it: the length in the
 * chosen units with its sign (a position can be negative), or a dash where
 * no length is set.
 */
export function lengthRowText(lengthFeet: number | null, units: MeasurementUnits): string {
  if (lengthFeet === null) return '–';
  const magnitude = formatLength(lengthFeet, units);
  return lengthFeet < 0 && magnitude !== formatLength(0, units) ? `-${magnitude}` : magnitude;
}

/** Square-feet display: rounded UP to the nearest whole number. */
export const formatSquareFeet = (area: number): string => Number.isFinite(area) ? String(Math.ceil(area)) : '—';

/** Long-form feet-and-inches: `40 ft 4 in`, `40 ft 4½ in`, inches omitted when zero (`40 ft`). Magnitude, like formatFeetInches. */
export const formatFeetInchesLong = (lengthFeet: number): string => {
  if (!Number.isFinite(lengthFeet)) return '—';
  const { feet, inches } = splitFeetInches(Math.abs(lengthFeet));
  return inches === 0 ? `${feet} ft` : `${feet} ft ${formatInches(inches)} in`;
};
